
import { Component, Prop, Vue, Watch } from "vue-property-decorator"
import { namespace, Getter, State } from "vuex-class"
import CalculationDataType from "@/types/field"

import { CaseColumn } from "@/types/caseColumns"
import {
	CalculationType,
	CalculationSortItemType,
	DataType,
	Insight,
	InsightCalculation,
	InsightCalculationTotal,
	GroupByDateFormat,
	Operator,
	operatorLabels,
	FormattingRule, InsightDetails, InsightSummary
} from "@/types/insights"

import vSelect from "vue-select"

import CalculationExpression from "../CalculationExpression.vue"

import { InsightCalculationItem, TimeSpentCalculation } from "@/types/insights/calculation"

@Component({
	components: {
		"v-select": vSelect,
		"calculation-expression": CalculationExpression
	}
})
export default class Calculation extends Vue {
	@Prop({ default: null })
	value!: InsightCalculationItem<InsightCalculation>

	@Prop({ default: null })
	insight!: InsightDetails

	@Prop()
	fields!: CaseColumn[]

	@Prop()
	groupByCalculationFields!: CaseColumn[]

	@Prop()
	calculationFields!: CaseColumn[]

	operators: any[] = operatorLabels;
	private Operator = Operator;

	private CalculationType = CalculationType;
	private CalculationSortItemType = CalculationSortItemType

	calculationTypeValues: any[] = [
		{
			type: CalculationType.Total,
			label: "Total",
			requiresField: true
		},
		{
			type: CalculationType.Count,
			label: "Count",
			requiresField: false
		},
		{
			type: CalculationType.Average,
			label: "Average",
			requiresField: true
		},
		{
			type: CalculationType.Min,
			label: "Minimum",
			requiresField: true
		},
		{
			type: CalculationType.Max,
			label: "Maximum",
			requiresField: true
		},
		{
			type: CalculationType.CountPerDay,
			label: "Count Per Day",
			requiresField: false,
			canUse: () => this.insight.clauses.some(c => c.fieldDataType === DataType.dateTime && c.operator === Operator.GreaterThan)
		}
	]

	get calculationTypes (): any[] {
		return this.calculationTypeValues.filter(calcType => calcType.canUse === undefined || calcType.canUse())
	}

	private calculatedFieldSupportsInsightCalculations (field: CaseColumn): Boolean {
		if (!field.fieldExtensions) {
			return false
		}

		const calculationDataType = this.getCalculationDataType(field)
		return calculationDataType === "number" || calculationDataType === "time-spent"
	}

	private getCalculationDataTypeFromFieldId (fieldId: string) : string | null {
		const strippedFieldId = this.stripFieldId(fieldId)

		const field = this.getFieldFromFieldId(strippedFieldId)
		if (!field || !field.fieldExtensions) {
			return null
		}
		const calculationDataType = CalculationDataType[field.fieldExtensions.calculationType as CalculationType]
		return calculationDataType
	}

	private stripFieldId (fieldId: string) : string {
		if (fieldId.startsWith("fields.")) {
			return fieldId.substring(7)
		}
		return fieldId
	}

	private getFieldFromFieldId (fieldId: string) : CaseColumn {
		const strippedFieldId = this.stripFieldId(fieldId)
		const field = this.fields.find(c => c.fieldId === strippedFieldId)
		if (!field) {
			Error(`Field ${fieldId} not found`)
		}
		return field!
	}

	private getTimeSpentTrackingField (timeSpentFieldId: string) : CaseColumn {
		const strippedFieldId = this.stripFieldId(timeSpentFieldId)
		const timeSpentField = this.fields.find(c => c.fieldId === strippedFieldId)

		const trackingFieldId = timeSpentField!.fieldExtensions!.trackingFieldId
		const trackingField = this.fields.find(c => c.fieldId === trackingFieldId)

		if (!trackingField) {
			throw new Error(`Could not find tracking field ${trackingFieldId}`)
		}
		return trackingField
	}

	private timeSpentCalculationChange (
		calculation: InsightCalculation,
		timeSpentCalculation: TimeSpentCalculation) {
		calculation.timeSpentCalculation = timeSpentCalculation
	}

	private getCalculationDataType (field: CaseColumn) : string | null {
		if (!field.fieldExtensions) {
			return null
		}
		const calculationDataType = CalculationDataType[field.fieldExtensions.calculationType as CalculationType]
		return calculationDataType
	}

	get calculationNumberColumns () {
		return this.insight.calculations.map(c => {
			return {
				label: c.label,
				key: `calculations[${c.key}]`,
				fieldDataType: DataType.number
			}
		})
	}

	private expressionCheckChanged (calculation: InsightCalculation, hasExpression: boolean) {
		if (hasExpression) {
			if (!calculation.expression) {
				calculation.expression = "{value}"
			}
		} else {
			calculation.expression = null
		}
	}

	private groupByCheckChanged (calculation: InsightCalculation, hasExpression: boolean) {
		if (hasExpression) {
			if (!calculation.groupBy) {
				calculation.groupBy = ""
			}
		} else {
			calculation.groupBy = null
		}
	}
}

